import { useState } from "react";
import { useT } from "../i18n";

/** One-tap share: native share sheet where the platform has one (mobile),
 * clipboard copy with inline feedback everywhere else. Pass the canonical
 * share URL (see @shared/share-links) so what people send matches what
 * crawlers unfurl. */
export function Share({ title, text, url }: { title: string; text?: string; url: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function onClick() {
    // Feature-detect at click time; a user cancelling the sheet should not
    // fall through to an unexpected clipboard write.
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch {
        return; // cancelled or unsupported payload — do nothing
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can still copy from the address bar */
    }
  }

  return (
    <button
      onClick={onClick}
      className="text-xs font-semibold px-2.5 py-1 rounded-full border border-border text-accent hover:border-accent transition whitespace-nowrap"
      title={t("share.tooltip")}
    >
      {copied ? `${t("share.copied")} ✓` : `🔗 ${t("share.share")}`}
    </button>
  );
}
